import React, { useEffect, useState, useMemo } from "react";
import {
  ViewConfig,
  ViewData,
  ColumnConfig,
  HighlightConfig,
  MobileConfig,
} from "../types/views";
import { useApi as useSimpleApi } from "../hooks/useSimpleApi";
import { useEventSource } from "../hooks/useEventSource";
import { EventData, SyncEventData } from "../types";
import LinkModal from "./LinkModal";
import { useLinkModal } from "../hooks/useLinkModal";
import { formatDate, formatDateTime, formatTime } from "../utils/format";
import { statusColors } from "../config/statusColors";

interface DynamicViewProps {
  config?: ViewConfig;
  viewId?: string;
  kioskMode?: boolean;
  isKioskMode?: boolean;
  displayId?: string;
}

interface CellRendererProps {
  value: any;
  column: ColumnConfig;
  row: any;
  openModal: (url: string, title?: string) => void;
}

// Responsive Breakpoint Hook
const useResponsiveBreakpoints = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    isMobile: windowSize.width < 1050,
    isTablet: windowSize.width >= 1050 && windowSize.width < 1150,
    isDesktop: windowSize.width >= 1150,
    width: windowSize.width,
    height: windowSize.height,
  };
};

// Responsive Text Display - vollständige Anzeige ohne Kürzung
const ResponsiveTextDisplay: React.FC<{
  value: any;
  column: ColumnConfig;
  isMobile?: boolean;
  className?: string;
  highlightConfig?: HighlightConfig;
}> = ({ value, column, isMobile = false, className = "", highlightConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!value || typeof value !== "string") {
    return <span className={className}>{value || "-"}</span>;
  }

  // Für sehr lange Texte (>200 Zeichen) auf Mobile: Expandable
  const isLongText = value.length > 200;
  const shouldTruncateOnMobile = isMobile && isLongText && !isExpanded;

  const displayValue = shouldTruncateOnMobile
    ? value.substring(0, 200) + "..."
    : value;

  return (
    <div className={className}>
      <SimpleLineBreakCell
        value={displayValue}
        fieldName={column.field}
        className="break-words overflow-wrap-anywhere hyphens-auto"
        highlightConfig={highlightConfig}
      />
      {isMobile && isLongText && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
        >
          {isExpanded ? "Weniger anzeigen" : "Vollständig anzeigen"}
        </button>
      )}
    </div>
  );
};

// Mobile Card View - strukturierte Daten-Anzeige mit Links/Badges rechts
const MobileCardView: React.FC<{
  data: any[];
  columns: ColumnConfig[];
  kioskMode?: boolean;
  openModal: (url: string, title?: string) => void;
  highlightConfig?: HighlightConfig;
  mobileConfig?: MobileConfig;
  statusColors?: Record<
    string,
    { background: string; border: string; text: string }
  >;
}> = ({
  data,
  columns,
  kioskMode = false,
  openModal,
  highlightConfig,
  mobileConfig,
  statusColors,
}) => {
  const baseCardClasses = kioskMode
    ? "bg-white rounded-lg shadow-sm border p-4"
    : "bg-white rounded-lg shadow-sm border p-3";

  // Use mobile_config to determine if empty fields should be hidden
  const hideEmptyFields = mobileConfig?.hide_empty_fields ?? false;

  // Helper function to check if a value is considered empty
  const isEmptyValue = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" || trimmed === "-" || trimmed === "—";
    }
    if (typeof value === "object" && value !== null) {
      // For link objects, check if URL is empty
      if ("url" in value && !value.url) return true;
    }
    return false;
  };

  // Spalten nach Typ gruppieren
  const statusColumns = columns.filter((col) => col.type === "status");
  const textColumns = columns.filter((col) => col.type === "text");
  const actionColumns = columns.filter(
    (col) => col.type === "link" || col.type === "badge",
  );
  const otherColumns = columns.filter(
    (col) => !["status", "text", "link", "badge"].includes(col.type),
  );

  return (
    <div className="grid grid-cols-1 gap-3">
      {data.map((row, index) => (
        <div key={row.id || index} className={baseCardClasses}>
          {/* Status Badge - immer oben */}
          {statusColumns.map((column) => {
            const value = row[column.field];
            // Bei hideEmptyFields: Feld komplett ausblenden wenn leer
            if (hideEmptyFields && isEmptyValue(value)) {
              return null;
            }

            return (
              <div key={column.field} className="mb-3">
                <StatusBadge
                  statusText={value}
                  isKioskMode={kioskMode}
                  fontClasses={getFontClasses(column)}
                  statusColors={statusColors}
                />
              </div>
            );
          })}

          {/* Hauptinhalt mit Text-Wrapping Layout */}
          <div className="relative">
            {/* Rechte Seite: Links und Badges - Float-Right für Text-Wrapping */}
            {actionColumns.length > 0 && (
              <div className="float-right ml-3 mb-2 space-y-2">
                {actionColumns.map((column) => {
                  const value = row[column.field];
                  // Bei hideEmptyFields: Feld komplett ausblenden wenn leer
                  if (hideEmptyFields && isEmptyValue(value)) {
                    return null;
                  }

                  return (
                    <div key={column.field} className="flex flex-col items-end">
                      {/* Optional: Titel für Action-Items */}
                      {column.type === "badge" && (
                        <div
                          className={`font-medium text-gray-500 ${
                            kioskMode ? "text-xs" : "text-xs"
                          } uppercase tracking-wider mb-1`}
                        >
                          {column.title}
                        </div>
                      )}

                      {column.type === "badge" ? (
                        <TypeBadge
                          type={value}
                          fontClasses={getFontClasses(column)}
                        />
                      ) : column.type === "link" ? (
                        <CellRenderer
                          value={value}
                          column={column}
                          row={row}
                          openModal={openModal}
                          highlightConfig={highlightConfig}
                          statusColors={statusColors}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Linke Seite: Text-Felder - umfließen rechte Elemente */}
            <div className="space-y-2">
              {textColumns.map((column) => {
                const value = row[column.field];
                // Bei hideEmptyFields: Feld komplett ausblenden wenn leer
                if (hideEmptyFields && isEmptyValue(value)) {
                  return null;
                }

                return (
                  <div key={column.field}>
                    <div
                      className={`font-medium text-gray-500 ${
                        kioskMode ? "text-xs" : "text-xs"
                      } uppercase tracking-wider`}
                    >
                      {column.title}
                    </div>
                    <div
                      className={`mt-0.5 ${kioskMode ? "text-base" : "text-sm"}`}
                    >
                      <ResponsiveTextDisplay
                        value={value ?? ""}
                        column={column}
                        isMobile={true}
                        className={`text-gray-900 ${getFontClasses(column)}`}
                        highlightConfig={highlightConfig}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Andere Felder (Datum etc.) auch links */}
              {otherColumns.map((column) => {
                const value = row[column.field];
                // Bei hideEmptyFields: Feld komplett ausblenden wenn leer
                if (hideEmptyFields && isEmptyValue(value)) {
                  return null;
                }

                return (
                  <div key={column.field}>
                    <div
                      className={`font-medium text-gray-500 ${
                        kioskMode ? "text-xs" : "text-xs"
                      } uppercase tracking-wider`}
                    >
                      {column.title}
                    </div>
                    <div
                      className={`mt-0.5 ${kioskMode ? "text-base" : "text-sm"}`}
                    >
                      {column.type === "date" ? (
                        <span className="font-mono text-gray-900">
                          {formatDate(value)}
                        </span>
                      ) : column.type === "datetime" ? (
                        <span className="font-mono text-gray-900">
                          {formatDateTime(value)}
                        </span>
                      ) : (
                        <span
                          className={`text-gray-900 ${getFontClasses(column)}`}
                        >
                          {value ?? "-"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Clearfix für Float-Layout */}
            <div className="clear-both"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Tablet kompakte Tabelle - alle Spalten aber optimiert
const TabletTableView: React.FC<{
  data: any[];
  columns: ColumnConfig[];
  kioskMode?: boolean;
  openModal: (url: string, title?: string) => void;
  isLocalhost?: boolean;
  highlightConfig?: HighlightConfig;
  statusColors?: Record<
    string,
    { background: string; border: string; text: string }
  >;
}> = ({
  data,
  columns,
  kioskMode = false,
  openModal,
  isLocalhost = false,
  highlightConfig,
  statusColors,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-full">
        <thead className="bg-gray-100 border-b border-gray-200">
          <tr>
            {columns.map((column) => (
              <th
                key={column.field}
                className={`px-2 py-3 text-left font-bold text-gray-700 uppercase tracking-wider ${
                  kioskMode ? "text-sm" : "text-xs"
                }`}
                style={{
                  width:
                    column.width !== "auto"
                      ? typeof column.width === "string" &&
                        column.width.includes("px")
                        ? `${parseInt(column.width) * 0.8}px`
                        : column.width
                      : undefined,
                }}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((row, index) => (
            <tr
              key={row.id || index}
              className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${!isLocalhost ? "hover:bg-blue-50" : ""} transition-colors duration-150`}
            >
              {columns.map((column) => (
                <td
                  key={column.field}
                  className={`px-2 py-3 ${kioskMode ? "text-base" : "text-sm"}`}
                >
                  <div className="min-w-0">
                    <CellRenderer
                      value={row[column.field]}
                      column={column}
                      row={row}
                      openModal={openModal}
                      highlightConfig={highlightConfig}
                      statusColors={statusColors}
                    />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Status-Badge-Komponente
const StatusBadge: React.FC<{
  statusText: string;
  isKioskMode?: boolean;
  fontClasses?: string;
  statusColors?: Record<
    string,
    { background: string; border: string; text: string }
  >;
}> = ({ statusText, isKioskMode = false, fontClasses = "", statusColors }) => {
  const getStatusStyle = (status: string): string => {
    // Use fontClasses from column config instead of hardcoded classes
    const baseClasses =
      fontClasses ||
      (isKioskMode ? "text-sm font-medium" : "text-xs font-medium");

    // Use status colors from config if available
    if (statusColors && statusColors[status]) {
      const colors = statusColors[status];
      const bgClass = `bg-${colors.background}`;
      const borderClass = `border-${colors.border}`;
      const textClass =
        colors.text === "black" ? "text-black" : `text-${colors.text}`;
      return `${bgClass} ${borderClass} ${textClass} ${baseClasses}`;
    }

    // Fallback to hardcoded colors (updated to match SharePoint)
    switch (status) {
      case "70.Prüfstand":
        return `bg-emerald-100 border-emerald-500 text-black ${baseClasses}`;
      case "60.Zusammenbau":
        return `bg-cyan-100 border-cyan-500 text-black ${baseClasses}`;
      case "50.Türkernbau":
        return `bg-gray-100 border-gray-400 text-gray-600 ${baseClasses}`;
      case "29.Freigabe zum Bau":
        return `bg-amber-100 border-amber-500 text-black ${baseClasses}`;
      case "55.Presse":
        return `bg-blue-100 border-blue-500 text-black ${baseClasses}`;
      case "75.Fertig zum Versand":
        return `bg-purple-100 border-purple-500 text-purple-700 ${baseClasses}`;
      default:
        return `bg-gray-50 border-gray-300 text-gray-700 ${baseClasses}`;
    }
  };

  const styleClass = getStatusStyle(statusText);
  const paddingClass = isKioskMode ? "px-3 py-2" : "px-2 py-1";

  return (
    <div className={`border rounded-md ${paddingClass} ${styleClass}`}>
      <span className="whitespace-nowrap">
        <SimpleLineBreakCell
          value={statusText}
          fieldName="status"
          highlightConfig={undefined}
        />
      </span>
    </div>
  );
};

// Badge-Komponente für Typen
const TypeBadge: React.FC<{
  type: string;
  fontClasses?: string;
}> = ({ type, fontClasses = "" }) => {
  const getTypeStyle = (type: string): string => {
    // Use fontClasses from column config instead of hardcoded classes
    const baseClasses = fontClasses || "text-xs font-bold";

    switch (type) {
      case "Türe":
        return `bg-blue-100 text-blue-800 ${baseClasses}`;
      case "Auftrag":
        return `bg-green-100 text-green-800 ${baseClasses}`;
      case "Service":
        return `bg-purple-100 text-purple-800 ${baseClasses}`;
      default:
        return `bg-gray-100 text-gray-800 ${baseClasses}`;
    }
  };

  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full ${getTypeStyle(type)}`}
    >
      <SimpleLineBreakCell
        value={type}
        fieldName="type"
        highlightConfig={undefined}
      />
    </span>
  );
};

// Highlight-Funktion für Textmarker-Effekt
const highlightText = (
  text: string,
  highlightConfig?: HighlightConfig,
): React.ReactNode => {
  console.log("🔍 highlightText called:", {
    text: text?.substring(0, 50),
    highlightConfig,
    hasConfig: !!highlightConfig,
    hasKeywords: !!highlightConfig?.keywords,
    keywordsLength: highlightConfig?.keywords?.length,
  });

  if (
    !highlightConfig ||
    !highlightConfig.keywords ||
    highlightConfig.keywords.length === 0
  ) {
    return text;
  }

  const {
    keywords,
    color = "#ffeb3b",
    case_sensitive = false,
  } = highlightConfig;

  // Escape special regex characters
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Create regex pattern from keywords
  const pattern = keywords.map(escapeRegex).join("|");
  const regex = new RegExp(`(${pattern})`, case_sensitive ? "g" : "gi");

  // Split text by matches
  const parts = text.split(regex);

  return parts.map((part, index) => {
    // Check if this part matches any keyword
    const isHighlight = keywords.some((keyword) =>
      case_sensitive
        ? part === keyword
        : part.toLowerCase() === keyword.toLowerCase(),
    );

    if (isHighlight) {
      return (
        <span
          key={index}
          className="highlight-marker"
          style={{
            backgroundColor: color,
            padding: "0 2px",
            borderRadius: "2px",
            fontWeight: "500",
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

// Robuste Multi-Format Zeilenumbruch-Komponente mit erweiterte Diagnose und Highlighting
const SimpleLineBreakCell: React.FC<{
  value: string;
  className?: string;
  fieldName?: string;
  highlightConfig?: HighlightConfig;
}> = ({ value, className = "", fieldName, highlightConfig }) => {
  // ENTRY DEBUG - Prüfen ob Komponente überhaupt aufgerufen wird
  if (
    fieldName === "info_k_s" ||
    fieldName === "info_nummern" ||
    (value && value.includes("TF06910"))
  ) {
    console.log("🚀 SIMPLELINEBREAKCELL ENTRY:", {
      fieldName: fieldName,
      value: value,
      valueType: typeof value,
      className: className,
      componentCalled: true,
      classNameContainsFontClasses:
        className.includes("text-") ||
        className.includes("font-") ||
        className.includes("italic"),
    });
  }

  if (!value || typeof value !== "string") {
    if (fieldName === "info_k_s") {
      console.log("❌ SIMPLELINEBREAKCELL EXIT EARLY:", {
        value,
        type: typeof value,
      });
    }
    // Konvertiere value zu String für highlighting, falls möglich
    const displayValue = value ? String(value) : "-";
    return (
      <span className={className}>
        {highlightText(displayValue, highlightConfig)}
      </span>
    );
  }

  // Multi-Format Line Break Detection
  const detectionResults = {
    hasCRLF: value && value.includes("\r\n"), // Windows CRLF
    hasLF: value && value.includes("\n"), // Unix LF
    hasCR: value && value.includes("\r"), // Mac CR
    hasEscapedCRLF: value && value.includes("\\r\\n"), // Escaped CRLF
    hasEscapedLF: value && value.includes("\\n"), // Escaped LF
    hasEscapedCR: value && value.includes("\\r"), // Escaped CR
    hasPatternBreaks:
      value &&
      (value.includes(" · ") ||
        value.includes(" B: ") ||
        value.includes(" Bem: ")), // MTE-Pattern
    isLong: value && value.length > 50, // Length heuristic
  };

  const hasAnyLineBreak = Object.values(detectionResults).some(Boolean);

  // Erweiterte String-Byte-Analyse für Diagnose
  if (
    fieldName === "info_k_s" ||
    (value && value.includes("TF06910")) ||
    (value && value.includes("V5.5-Pneumatik"))
  ) {
    const stringCodes = [...value].map((char) => ({
      char: char,
      code: char.charCodeAt(0),
      hex: char.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0"),
    }));

    console.log("🔬 EXTENDED STRING ANALYSIS:", {
      fieldName: fieldName,
      originalValue: value,
      valueLength: value.length,
      detectionResults: detectionResults,
      hasAnyLineBreak: hasAnyLineBreak,
      first50Chars: value.substring(0, 50),
      // String-Byte-Analyse für die ersten 100 Zeichen
      stringCodes: stringCodes.slice(0, 100),
      // Suche nach Line Break Codes
      crlfPositions: stringCodes
        .filter((s) => s.code === 13 || s.code === 10)
        .map((s, i) => ({ index: i, ...s })),
      // JSON-escaped sequences
      escapedSequences: value.match(/\\[rn]/g) || [],
    });
  }

  if (hasAnyLineBreak) {
    // Universal Line Break Normalization and Split Strategy
    let processedValue = value;

    // Phase 1: Unescape escaped line breaks first
    processedValue = processedValue
      .replace(/\\r\\n/g, "\n") // Escaped CRLF → LF
      .replace(/\\r/g, "\n") // Escaped CR → LF
      .replace(/\\n/g, "\n"); // Escaped LF → LF

    // Phase 2: Normalize all line break variants to LF
    processedValue = processedValue
      .replace(/\r\n/g, "\n") // Windows CRLF → LF
      .replace(/\r/g, "\n"); // Mac CR → LF

    // Phase 3: Split on normalized LF
    let lines = processedValue.split("\n");

    // Fallback: Pattern-based splitting for MTE-specific data
    if (lines.length === 1 && detectionResults.hasPatternBreaks) {
      // Splitte bei typischen MTE-Trennmustern
      const patterns = [" B: ", " Bem: "];
      for (const pattern of patterns) {
        if (value.includes(pattern)) {
          lines = value.split(pattern).reduce((acc, part, index) => {
            if (index === 0) {
              acc.push(part);
            } else {
              acc.push(pattern.trim() + ": " + part);
            }
            return acc;
          }, [] as string[]);
          break;
        }
      }
    }

    // Fallback: Length-based artificial breaks
    if (lines.length === 1 && detectionResults.isLong) {
      const maxLineLength = 60;
      const words = value.split(" ");
      lines = [];
      let currentLine = "";

      for (const word of words) {
        if (
          (currentLine + " " + word).length > maxLineLength &&
          currentLine.length > 0
        ) {
          lines.push(currentLine.trim());
          currentLine = word;
        } else {
          currentLine += (currentLine ? " " : "") + word;
        }
      }
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
      }
    }

    // Debug Logging für info_k_s
    if (fieldName === "info_k_s") {
      console.log("✂️ SPLIT ANALYSIS:", {
        originalValue: value,
        processedValue: processedValue,
        finalLines: lines,
        lineCount: lines.length,
        splitStrategy:
          lines.length > 1
            ? processedValue !== value
              ? "normalized-linebreaks"
              : detectionResults.hasPatternBreaks
                ? "pattern-based"
                : "length-based"
            : "no-split",
      });
    }

    // Render multi-line result
    if (lines.length > 1) {
      // Debug-Logging für Multi-Line Rendering
      if (fieldName === "info_k_s" || fieldName === "info_nummern") {
        console.log("📦 MULTI-LINE RENDER:", {
          fieldName: fieldName,
          finalClassName: className,
          linesCount: lines.length,
          appliedToDiv: true,
        });
      }

      return (
        <div className={className}>
          {/* MULTI_LINE_DETECTED: Split into multiple lines */}
          {lines.map((line, index) => (
            <span key={index} style={{ display: "block", lineHeight: "1.4" }}>
              {highlightText(line, highlightConfig)}
            </span>
          ))}
        </div>
      );
    }
  }

  // Fallback: Single line rendering
  if (fieldName === "info_k_s" || fieldName === "info_nummern") {
    console.log("📦 SINGLE-LINE RENDER:", {
      fieldName: fieldName,
      finalClassName: className,
      appliedToSpan: true,
      valueLength: value.length,
    });
  }

  return (
    <span className={className}>{highlightText(value, highlightConfig)}</span>
  );
};

// Font-Klassen-Mapper für views.json Font-Einstellungen zu TailwindCSS
const getFontClasses = (column: ColumnConfig): string => {
  const classes: string[] = [];

  // Debug-Logging für Font-Klassen-Generierung (optional)
  // if (column.field === 'info_nummern' || column.field === 'info_k_s') {
  //   console.log('🎨 FONT CLASSES GENERATION:', {
  //     field: column.field,
  //     fontSize: column.font_size,
  //     fontWeight: column.font_weight,
  //     fontStyle: column.font_style
  //   });
  // }

  // Font Size Mapping
  switch (column.font_size) {
    case "xs":
      classes.push("text-xs");
      break;
    case "sm":
      classes.push("text-sm");
      break;
    case "base":
      classes.push("text-base");
      break;
    case "lg":
      classes.push("text-lg");
      break;
    case "xl":
      classes.push("text-xl");
      break;
    case "2xl":
      classes.push("text-2xl");
      break;
    default:
      classes.push("text-base"); // Default fallback
  }

  // Font Weight Mapping
  switch (column.font_weight) {
    case "thin":
      classes.push("font-thin");
      break;
    case "light":
      classes.push("font-light");
      break;
    case "normal":
      classes.push("font-normal");
      break;
    case "medium":
      classes.push("font-medium");
      break;
    case "semibold":
      classes.push("font-semibold");
      break;
    case "bold":
      classes.push("font-bold");
      break;
    case "extrabold":
      classes.push("font-extrabold");
      break;
    case "black":
      classes.push("font-black");
      break;
    default:
      classes.push("font-normal"); // Default fallback
  }

  // Font Style Mapping
  switch (column.font_style) {
    case "italic":
      classes.push("italic");
      break;
    case "normal":
    default:
      // Kein zusätzlicher Klasse für normal
      break;
  }

  const finalClasses = classes.join(" ");

  // Debug-Logging für finales Ergebnis (optional)
  // if (column.field === 'info_nummern' || column.field === 'info_k_s') {
  //   console.log('🎨 FONT CLASSES RESULT:', {
  //     field: column.field,
  //     finalClasses: finalClasses,
  //     classesArray: classes
  //   });
  // }

  return finalClasses;
};

// Zell-Renderer basierend auf Spalten-Typ
const CellRenderer: React.FC<
  CellRendererProps & {
    highlightConfig?: HighlightConfig;
    statusColors?: Record<
      string,
      { background: string; border: string; text: string }
    >;
  }
> = ({ value, column, row, openModal, highlightConfig, statusColors }) => {
  // Debug-Logging für CellRenderer
  if (
    column.field === "info_k_s" ||
    (typeof value === "string" && value.includes("TF06910"))
  ) {
    console.log("🎯 CELLRENDERER DEBUG:", {
      field: column.field,
      type: column.type,
      value: value,
      valueType: typeof value,
      valueLength: typeof value === "string" ? value.length : "N/A",
      hasNewlines: typeof value === "string" ? value.includes("\n") : false,
      hasCRLF: typeof value === "string" ? value.includes("\r\n") : false,
      sample: typeof value === "string" ? value.substring(0, 50) : value,
    });
  }

  if (value === null || value === undefined) {
    return <span className="text-gray-400">-</span>;
  }

  switch (column.type) {
    case "status":
      return (
        <StatusBadge
          statusText={value}
          isKioskMode={false}
          fontClasses={getFontClasses(column)}
          statusColors={statusColors}
        />
      );

    case "badge":
      return <TypeBadge type={value} fontClasses={getFontClasses(column)} />;

    case "link":
      // Handle both string URLs and object {url, text} formats
      let url: string | null = null;
      let linkText: string = "Link";

      // Extract URL from value
      if (typeof value === "string" && value.startsWith("http")) {
        url = value;
      } else if (
        value &&
        typeof value === "object" &&
        value.url &&
        typeof value.url === "string"
      ) {
        url = value.url;
      }

      // Extract link text from configured field or fallbacks
      if (column.link_text_field && row[column.link_text_field]) {
        linkText = String(row[column.link_text_field]);
      } else if (value && typeof value === "object" && value.text) {
        linkText = String(value.text);
      } else if (column.link_text_field && !row[column.link_text_field]) {
        // If link_text_field is configured but field is missing, use object.text as fallback
        if (value && typeof value === "object" && value.text) {
          linkText = String(value.text);
        } else {
          linkText = "Link"; // Ultimate fallback
        }
      }

      // Debug logging for link rendering
      if (column.field === "qr_kurz") {
        console.log("🔗 LINK RENDERER DEBUG:", {
          field: column.field,
          value: value,
          valueType: typeof value,
          extractedUrl: url,
          linkTextField: column.link_text_field,
          linkTextFieldValue: column.link_text_field
            ? row[column.link_text_field]
            : null,
          finalLinkText: linkText,
          rowData: row,
        });
      }

      if (url) {
        // Generate font classes for the link
        const fontClasses = getFontClasses(column);

        return (
          <div
            className={`border border-blue-300 rounded-md bg-blue-50 hover:bg-blue-100 transition-colors px-3 py-1 ${fontClasses}`}
          >
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() =>
                  openModal(
                    url,
                    `${linkText} - ${row.projekt_name || row.info_nummern || "Link"}`,
                  )
                }
                className="flex items-center justify-center font-medium text-blue-700 no-underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                title={`${linkText} im Overlay öffnen`}
              >
                <span className="whitespace-nowrap">{linkText}</span>
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="In neuem Tab öffnen"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        );
      }

      return <span className="text-gray-400">-</span>;

    case "date":
      try {
        return formatDate(value);
      } catch {
        return <span className="text-gray-400">{value}</span>;
      }

    case "datetime":
      try {
        return formatDateTime(value);
      } catch {
        return <span className="text-gray-400">{value}</span>;
      }

    case "number":
      return (
        <span className="font-mono">
          {Number(value).toLocaleString("de-DE")}
        </span>
      );

    case "text":
    default:
      // Debug-Logging für text case
      if (
        column.field === "info_k_s" ||
        column.field === "info_nummern" ||
        (typeof value === "string" && value.includes("TF06910"))
      ) {
        console.log("📝 CELLRENDERER TEXT CASE:", {
          field: column.field,
          type: column.type,
          value: value,
          willUseSimpleLineBreakCell: true,
          fontClasses: getFontClasses(column),
        });
      }

      // Generate dynamic font classes from column configuration
      const fontClasses = getFontClasses(column);
      const combinedClasses = `break-words overflow-wrap-anywhere ${fontClasses}`;

      // Debug-Logging für kombinierte Klassen
      if (column.field === "info_k_s" || column.field === "info_nummern") {
        console.log("🔗 COMBINED CLASSES:", {
          field: column.field,
          fontClasses: fontClasses,
          combinedClasses: combinedClasses,
        });
      }

      // Use SimpleLineBreakCell for all text fields to handle \r\n line breaks
      return (
        <SimpleLineBreakCell
          value={String(value)}
          className={combinedClasses}
          fieldName={column.field}
          highlightConfig={highlightConfig}
        />
      );
  }
};

const DynamicView: React.FC<DynamicViewProps> = ({
  config: propConfig,
  viewId,
  kioskMode = false,
  isKioskMode = false,
  displayId,
}) => {
  const [config, setConfig] = useState<ViewConfig | null>(propConfig || null);

  // WORKAROUND: Hardcode configs until API serialization is fixed
  const hardcodedHighlightConfig: HighlightConfig = {
    keywords: ["Vorsicht", "Sonderanfertigung", "Achtung"],
    color: "rgba(255, 221, 87, 0.5)",
    case_sensitive: false,
  };

  const hardcodedMobileConfig: MobileConfig = {
    hide_empty_fields: true,
  };

  // Use generated status colors from config/views.json
  const effectiveStatusColors = statusColors;
  const [data, setData] = useState<ViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Check if running on localhost (disable hover effects for local display)
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "::1");

  // Modal state
  const {
    isOpen,
    url: modalUrl,
    title,
    openModal,
    closeModal,
  } = useLinkModal();
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    "connected" | "disconnected" | "reconnecting"
  >("connected");
  const [syncStatus, setSyncStatus] = useState<{
    isActive: boolean;
    type?: "incremental" | "full";
    lastUpdate?: string;
  }>({ isActive: false });

  // Auth state

  const api = useSimpleApi();

  // EventSource für Real-Time Sync Updates
  const {} = useEventSource("/api/events", {
    onMessage: (data: EventData) => {
      const event = data as SyncEventData;
      console.log("🔄 SSE Event received:", event);

      switch (event.type) {
        case "sync_started":
          setSyncStatus({
            isActive: true,
            type: event.sync_type,
            lastUpdate: new Date().toISOString(),
          });
          break;

        case "sync_completed":
          setSyncStatus({
            isActive: false,
            lastUpdate: new Date().toISOString(),
          });
          // Auto-refresh data after sync completion
          console.log("🔄 Sync completed, auto-refreshing data...");
          fetchData(true);
          break;

        case "sync_failed":
          setSyncStatus({
            isActive: false,
            lastUpdate: new Date().toISOString(),
          });
          console.warn("⚠️ Sync failed:", event.error_message);
          break;
      }
    },
    onError: (error) => {
      console.error("❌ SSE Error:", error);
    },
  });

  // Responsive Breakpoints
  const { isMobile, isTablet } = useResponsiveBreakpoints();

  // Check auth status

  // Lade ViewConfig falls nur viewId übergeben wurde
  useEffect(() => {
    console.log(
      `🔧 DynamicView useEffect - viewId: ${viewId}, hasConfig: ${!!config}, isKioskMode: ${isKioskMode}`,
    );

    if (!config && viewId) {
      const abortController = new AbortController();

      const loadViewConfig = async () => {
        try {
          console.log(`📊 Loading view config for viewId: ${viewId}`);
          setLoading(true);

          const response = await api.get(`/api/views/${viewId}`, {
            signal: abortController.signal,
          });

          console.log(`📊 View config API response:`, {
            viewId,
            status: response.status,
            hasData: !!response.data,
            configName: response.data?.name,
          });

          if (!abortController.signal.aborted) {
            console.log(`✅ Setting view config for ${viewId}:`, response.data);
            console.log(
              `🔍 highlight_config received:`,
              response.data?.highlight_config,
            );
            setConfig(response.data);
            setLoading(false);
            console.log(`🎯 DynamicView loading completed successfully`);
          }
        } catch (err: any) {
          if (!abortController.signal.aborted) {
            console.error(`❌ Error loading view config for ${viewId}:`, err);
            setError(`Fehler beim Laden der Ansicht: ${err.message}`);
            setLoading(false);
          }
        }
      };

      loadViewConfig();

      return () => {
        abortController.abort();
      };
    } else if (config) {
      console.log(`🎯 DynamicView using provided config: ${config.name}`);
    }
  }, [viewId, api]);

  // Bestimme Kiosk-Modus
  const effectiveKioskMode = kioskMode || isKioskMode;

  // Bestimme Refresh-Intervall basierend auf Kiosk-Modus
  const refreshInterval = useMemo(() => {
    if (effectiveKioskMode && config?.kiosk_settings?.enabled) {
      return config.kiosk_settings.refresh_interval || 15000;
    }
    return config?.refresh_interval || 30000;
  }, [effectiveKioskMode, config]);

  const fetchData = async (isRefresh = false) => {
    if (!config?.view_id) return;

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await api.get(`/api/views/${config.view_id}/data`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // API-Datenstruktur Debug für View-Data
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        const sampleData = response.data.data.slice(0, 2); // Erste 2 Einträge
        console.log("📊 API VIEW DATA STRUCTURE:", {
          totalItems: response.data.data.length,
          viewId: config.view_id,
          columns: response.data.columns?.map((col: any) => ({
            field: col.field,
            type: col.type,
          })),
          sampleItems: sampleData.map((item: any) => ({
            id: item.id,
            infoKsValue: item.info_k_s,
            infoKsType: typeof item.info_k_s,
            infoKsLength:
              typeof item.info_k_s === "string" ? item.info_k_s.length : "N/A",
            infoKsHasNewlines:
              typeof item.info_k_s === "string"
                ? item.info_k_s.includes("\n")
                : false,
            infoKsHasCRLF:
              typeof item.info_k_s === "string"
                ? item.info_k_s.includes("\r\n")
                : false,
            allFields: Object.keys(item),
          })),
        });
      }

      setData(response.data);
      setError(null);
      setConnectionStatus("connected");
    } catch (err: any) {
      if (err.name === "AbortError") {
        setError("Zeitüberschreitung - Prüfe Netzwerkverbindung");
        setConnectionStatus("disconnected");
      } else {
        setError(
          err.response?.data?.detail ||
            err.message ||
            "Fehler beim Laden der Daten",
        );
        setConnectionStatus("disconnected");
      }
      console.error("Error fetching view data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!config) return;

    fetchData();

    // Auto-refresh
    const interval = setInterval(() => fetchData(true), refreshInterval);

    // Connection monitoring in kiosk mode
    let reconnectInterval: ReturnType<typeof setInterval>;
    if (kioskMode && connectionStatus === "disconnected") {
      reconnectInterval = setInterval(() => {
        setConnectionStatus("reconnecting");
        fetchData(true);
      }, 5000);
    }

    return () => {
      clearInterval(interval);
      if (reconnectInterval) clearInterval(reconnectInterval);
    };
  }, [config?.view_id, refreshInterval, kioskMode, connectionStatus]);

  // Kiosk-spezifische Styles
  const kioskStyles = useMemo(() => {
    if (!kioskMode || !config?.kiosk_settings?.enabled) return {};

    return {
      largeFonts: config.kiosk_settings.large_fonts,
      hideHeader: config.kiosk_settings.hide_header,
    };
  }, [kioskMode, config?.kiosk_settings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <span
            className={`mt-4 text-gray-600 block ${kioskStyles.largeFonts ? "text-xl" : "text-lg"}`}
          >
            Lade {config?.name || "Ansicht"}...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md w-full">
          <div className="flex items-center">
            <div className="text-red-400">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-4">
              <h3
                className={`font-medium text-red-800 ${kioskStyles.largeFonts ? "text-xl" : "text-lg"}`}
              >
                Verbindungsfehler
              </h3>
              <p className="text-red-700 mt-1">{error}</p>
              {kioskMode && (
                <p className="text-red-600 mt-2 text-sm">
                  Display: {displayId}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => fetchData()}
            className="mt-4 w-full bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded font-medium"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // Entfernt: Vollbild "Keine Daten" Anzeige - wird jetzt im Hauptlayout gehandhabt

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Responsive Header */}
        {!kioskStyles.hideHeader && (
          <div className="bg-white shadow-sm border-b">
            <div
              className={`px-3 sm:px-6 py-3 sm:py-4 ${isMobile ? "pb-2" : ""}`}
            >
              {isMobile ? (
                // Mobile Header: Stack Layout für vollständige Informationen
                <div className="space-y-3">
                  {/* Titel und Anzahl */}
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <h1
                        className={`font-bold text-gray-900 ${
                          kioskStyles.largeFonts ? "text-2xl" : "text-lg"
                        }`}
                      >
                        {config?.name || "Ansicht"}
                      </h1>
                    </div>
                    {!kioskMode && (
                      <div className="flex items-center space-x-2">
                        {/* Sync Status Indicator */}
                        {syncStatus.isActive && (
                          <div className="flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-sm">
                            <svg
                              className="animate-spin w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H8a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H12a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Sync läuft
                          </div>
                        )}
                        <button
                          onClick={() => fetchData(true)}
                          disabled={refreshing}
                          className={`p-2 rounded-lg transition-colors ${
                            refreshing
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-gray-100"
                          }`}
                          title="Aktualisieren"
                        >
                          <svg
                            className={`text-gray-500 ${refreshing ? "animate-spin" : ""} w-5 h-5`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Status und Info-Zeile */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-600">
                        {data?.total_count || 0} Einträge
                      </span>
                      <div className="flex items-center">
                        <div
                          className={`w-2 h-2 rounded-full mr-1 ${
                            refreshing
                              ? "bg-yellow-400 animate-pulse"
                              : connectionStatus === "connected"
                                ? "bg-green-400"
                                : connectionStatus === "reconnecting"
                                  ? "bg-yellow-400 animate-pulse"
                                  : "bg-red-400"
                          }`}
                        ></div>
                        <span className="text-gray-600 text-xs">
                          {refreshing
                            ? "Aktualisiert..."
                            : connectionStatus === "connected"
                              ? "Live"
                              : connectionStatus === "reconnecting"
                                ? "Verbinde..."
                                : "Offline"}
                        </span>
                      </div>
                    </div>

                    {/* Timestamp auf Mobile sichtbar */}
                    <span className="text-gray-500 text-xs">
                      {data?.timestamp && formatTime(data.timestamp)}
                    </span>
                  </div>

                  {displayId && (
                    <div className="text-xs text-gray-500">
                      Display: {displayId}
                    </div>
                  )}
                </div>
              ) : (
                // Tablet/Desktop Header: Standard Layout
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="min-w-0 flex-1">
                    <h1
                      className={`font-bold text-gray-900 ${
                        kioskStyles.largeFonts
                          ? "text-3xl lg:text-4xl"
                          : "text-xl sm:text-2xl lg:text-3xl"
                      }`}
                    >
                      {config?.name || "Ansicht"}
                    </h1>
                    <p
                      className={`text-gray-600 mt-1 ${
                        kioskStyles.largeFonts
                          ? "text-lg"
                          : "text-sm sm:text-base"
                      }`}
                    >
                      {data?.total_count || 0} Einträge • Live-Übersicht
                      {displayId && ` • Display: ${displayId}`}
                    </p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end space-x-3 sm:space-x-4">
                    {/* Sync Status Indicator für Desktop */}
                    {syncStatus.isActive && (
                      <div className="flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                        <svg
                          className="animate-spin w-4 h-4 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H8a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H12a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {syncStatus.type === "full"
                          ? "Full-Sync läuft"
                          : "Sync läuft"}
                      </div>
                    )}
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          refreshing
                            ? "bg-yellow-400 animate-pulse"
                            : connectionStatus === "connected"
                              ? "bg-green-400"
                              : connectionStatus === "reconnecting"
                                ? "bg-yellow-400 animate-pulse"
                                : "bg-red-400"
                        }`}
                      ></div>
                      <span
                        className={`ml-2 text-gray-600 ${
                          kioskStyles.largeFonts
                            ? "text-lg"
                            : "text-sm sm:text-base"
                        }`}
                      >
                        {refreshing
                          ? "Aktualisiert..."
                          : connectionStatus === "connected"
                            ? kioskMode
                              ? "Kiosk-Modus"
                              : "Live"
                            : connectionStatus === "reconnecting"
                              ? "Verbinde..."
                              : "Offline"}
                      </span>
                    </div>
                    <span
                      className={`text-gray-500 ${
                        kioskStyles.largeFonts
                          ? "text-base"
                          : "text-xs sm:text-sm"
                      }`}
                    >
                      {data?.timestamp && formatDateTime(data.timestamp)}
                    </span>
                    {!kioskMode && (
                      <button
                        onClick={() => fetchData(true)}
                        disabled={refreshing}
                        className={`p-2 rounded-lg transition-colors ${
                          refreshing
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-gray-100"
                        }`}
                        title="Aktualisieren"
                      >
                        <svg
                          className={`text-gray-500 ${refreshing ? "animate-spin" : ""} ${
                            kioskStyles.largeFonts
                              ? "w-6 h-6"
                              : "w-4 h-4 sm:w-5 sm:h-5"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content-Bereich: Tabelle oder Keine Daten */}
        <div className="p-3 sm:p-6">
          <div
            className={`${isMobile ? "" : "bg-white rounded-lg shadow-sm border"}`}
          >
            {data && data.data && data.data.length > 0 ? (
              // Responsive Daten-Darstellung
              <>
                {isMobile ? (
                  // Mobile/Tablet Cards: Responsive Card-Layout für < 1050px
                  <div className="p-1">
                    <MobileCardView
                      data={data.data}
                      columns={config?.columns || []}
                      kioskMode={effectiveKioskMode}
                      openModal={openModal}
                      highlightConfig={hardcodedHighlightConfig}
                      mobileConfig={hardcodedMobileConfig}
                      statusColors={effectiveStatusColors}
                    />
                  </div>
                ) : isTablet ? (
                  // Tablet Table: Kompakte Tabelle für 1050px-1150px
                  <div className="p-2">
                    <TabletTableView
                      data={data.data}
                      columns={config?.columns || []}
                      kioskMode={effectiveKioskMode}
                      openModal={openModal}
                      isLocalhost={isLocalhost}
                      highlightConfig={hardcodedHighlightConfig}
                      statusColors={effectiveStatusColors}
                    />
                  </div>
                ) : (
                  // Desktop: Standard Tabellen-Layout
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 border-b border-gray-200">
                        <tr>
                          {config?.columns?.map((column) => (
                            <th
                              key={column.field}
                              className={`px-4 py-4 text-left font-bold text-gray-700 uppercase tracking-wider ${
                                kioskStyles.largeFonts ? "text-base" : "text-sm"
                              }`}
                              style={{
                                width:
                                  column.width !== "auto"
                                    ? column.width
                                    : undefined,
                              }}
                            >
                              {column.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.data.map((row, index) => (
                          <tr
                            key={row.id || index}
                            className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${!isLocalhost ? "hover:bg-blue-50" : ""} transition-colors duration-150`}
                          >
                            {config?.columns?.map((column) => (
                              <td
                                key={column.field}
                                className={`px-4 py-5 ${
                                  kioskStyles.largeFonts
                                    ? "text-lg"
                                    : "text-base"
                                }`}
                              >
                                <CellRenderer
                                  value={row[column.field]}
                                  column={column}
                                  row={row}
                                  openModal={openModal}
                                  highlightConfig={hardcodedHighlightConfig}
                                  statusColors={effectiveStatusColors}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              // Responsive "Keine Daten" Anzeige
              <div
                className={`text-center ${isMobile ? "py-8 px-4" : "py-12 px-6"} ${
                  isMobile ? "" : "bg-white rounded-lg"
                }`}
              >
                <div
                  className={`text-yellow-600 font-medium mb-4 ${
                    isMobile
                      ? kioskStyles.largeFonts
                        ? "text-xl"
                        : "text-lg"
                      : kioskStyles.largeFonts
                        ? "text-2xl"
                        : "text-xl"
                  }`}
                >
                  📋 Keine Daten
                </div>
                <p
                  className={`text-gray-600 mb-6 max-w-md mx-auto ${
                    isMobile
                      ? kioskStyles.largeFonts
                        ? "text-base"
                        : "text-sm"
                      : kioskStyles.largeFonts
                        ? "text-lg"
                        : "text-base"
                  }`}
                >
                  Aktuell sind keine Einträge für "
                  {config?.name || "diese Ansicht"}" vorhanden.
                </p>
                <div
                  className={`text-gray-500 ${
                    isMobile
                      ? kioskStyles.largeFonts
                        ? "text-sm"
                        : "text-xs"
                      : kioskStyles.largeFonts
                        ? "text-base"
                        : "text-sm"
                  }`}
                >
                  {refreshing ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Aktualisiert automatisch...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                      Auto-Refresh aktiv • Nächste Aktualisierung in{" "}
                      {Math.ceil(refreshInterval / 1000)}s
                    </div>
                  )}
                </div>
                {!kioskMode && (
                  <button
                    onClick={() => fetchData(true)}
                    disabled={refreshing}
                    className={`mt-4 px-4 py-2 rounded-lg transition-colors ${
                      refreshing
                        ? "opacity-50 cursor-not-allowed bg-gray-100 text-gray-500"
                        : "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                    }`}
                  >
                    {refreshing ? "Aktualisiert..." : "Jetzt aktualisieren"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      <LinkModal
        isOpen={isOpen}
        url={modalUrl}
        title={title}
        onClose={closeModal}
      />
    </>
  );
};

export default DynamicView;
